/**
 * useInvoiceFormState – sub-hooks
 *
 * Grouped useState declarations extracted from useInvoiceFormState.ts to keep
 * the main hook file under the max-lines limit.
 */

import { useState, useMemo, useRef } from 'react'

import { countries } from '@elsapiens/ui'

import type { Address } from '@/gen/proto'

import {
  parseLocalDate,
  parsePaymentTermsToDays,
  type LineItem,
} from './invoice-form-helpers'
import {
  buildAddressSnapshot,
  buildInitialLineItems,
  buildReconstructedPartyAddress,
  buildReconstructedShipToAddress,
  defaultInvoiceType,
  defaultExportType,
} from './useInvoiceFormState-helpers'
import { useSettings } from '../../../stores/settings-store'

import type { InvoiceFormState } from './useInvoiceFormState'
import type { AddressSnapshot } from './useInvoiceFormState-types'
import type { Account } from '../../../services/account-service'
import type { BankAccount } from '../../../services/bank-account-service'
import type { Invoice, InvoiceType } from '../../../types/billing'
import type { CustomerPurchaseOrder } from '../../../types/customer-purchase-order'

export interface SettingsLike { defaultDeliveryType?: string; businessType?: string }

// ─────────────────────────────────────────────────────────────────────────────
// Core (header + money + terms + misc)
// ─────────────────────────────────────────────────────────────────────────────

export function useCoreFormState(
  initialData: Partial<Invoice> | undefined,
  baseCurrency: string,
  isVendor: boolean,
  settings: SettingsLike,
): Pick<InvoiceFormState,
  | 'invoiceType' | 'setInvoiceType'
  | 'invoiceNumber' | 'setInvoiceNumber'
  | 'supplierBillNumber' | 'setSupplierBillNumber'
  | 'changeNotes' | 'setChangeNotes'
  | 'invoiceDate' | 'setInvoiceDate'
  | 'dueDate' | 'setDueDate'
  | 'selectedPartyId' | 'setSelectedPartyId'
  | 'partyRole' | 'setPartyRole' | 'roleOptions' | 'setRoleOptions'
  | 'currency' | 'setCurrency'
  | 'exchangeRate' | 'setExchangeRate'
  | 'lineItems' | 'setLineItems'
  | 'notes' | 'setNotes'
  | 'termsConditions' | 'setTermsConditions'
  | 'paymentTerms' | 'setPaymentTerms'
  | 'selectedPaymentTermValue' | 'setSelectedPaymentTermValue'
  | 'customCreditDays' | 'setCustomCreditDays'
  | 'poNumber' | 'setPONumber'
  | 'partyVendorCode' | 'setPartyVendorCode'
  | 'isRoundOffEnabled' | 'setIsRoundOffEnabled'
  | 'placeOfSupply' | 'setPlaceOfSupply'
> {
  const headerState = useInvoiceHeaderState(initialData, isVendor, settings)
  const moneyState = useInvoiceMoneyState(initialData, baseCurrency)
  const termsState = useInvoiceTermsState(initialData)
  const miscState = useInvoiceMiscState(initialData)
  return { ...headerState, ...moneyState, ...termsState, ...miscState }
}

function useInvoiceHeaderState(
  initialData: Partial<Invoice> | undefined,
  isVendor: boolean,
  settings: SettingsLike,
): Pick<InvoiceFormState,
  'invoiceType' | 'setInvoiceType' | 'invoiceNumber' | 'setInvoiceNumber'
  | 'supplierBillNumber' | 'setSupplierBillNumber'
  | 'changeNotes' | 'setChangeNotes' | 'invoiceDate' | 'setInvoiceDate'
  | 'dueDate' | 'setDueDate' | 'selectedPartyId' | 'setSelectedPartyId'
  | 'partyRole' | 'setPartyRole' | 'roleOptions' | 'setRoleOptions'
> {
  const [invoiceType, setInvoiceType] = useState<InvoiceType>(() =>
    defaultInvoiceType(initialData, isVendor, settings.defaultDeliveryType),
  )
  const [invoiceNumber, setInvoiceNumber] = useState(initialData?.invoiceNumber ?? '')
  const [supplierBillNumber, setSupplierBillNumber] = useState(initialData?.supplierBillNumber ?? '')
  const [changeNotes, setChangeNotes] = useState('')
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(
    initialData?.invoiceDate ? parseLocalDate(initialData.invoiceDate) : new Date(),
  )
  const [dueDate, setDueDate] = useState<Date | null>(
    initialData?.dueDate ? parseLocalDate(initialData.dueDate) : null,
  )
  const [selectedPartyId, setSelectedPartyId] = useState(initialData?.partyId ?? '')
  const [partyRole, setPartyRole] = useState(initialData?.partyRole ?? '')
  const [roleOptions, setRoleOptions] = useState<Array<{ value: string; label: string }>>([])
  return {
    invoiceType, setInvoiceType, invoiceNumber, setInvoiceNumber,
    supplierBillNumber, setSupplierBillNumber,
    changeNotes, setChangeNotes, invoiceDate, setInvoiceDate,
    dueDate, setDueDate, selectedPartyId, setSelectedPartyId,
    partyRole, setPartyRole, roleOptions, setRoleOptions,
  }
}

function useInvoiceMoneyState(
  initialData: Partial<Invoice> | undefined,
  baseCurrency: string,
): Pick<InvoiceFormState,
  'currency' | 'setCurrency' | 'exchangeRate' | 'setExchangeRate'
  | 'lineItems' | 'setLineItems'
> {
  const [currency, setCurrency] = useState(initialData?.currency ?? baseCurrency)
  const [exchangeRate, setExchangeRate] = useState(initialData?.exchangeRate ?? '1.0')
  const [lineItems, setLineItems] = useState<LineItem[]>(() =>
    buildInitialLineItems(initialData, baseCurrency),
  )
  return { currency, setCurrency, exchangeRate, setExchangeRate, lineItems, setLineItems }
}

function useInvoiceTermsState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'notes' | 'setNotes' | 'termsConditions' | 'setTermsConditions'
  | 'paymentTerms' | 'setPaymentTerms'
  | 'selectedPaymentTermValue' | 'setSelectedPaymentTermValue'
  | 'customCreditDays' | 'setCustomCreditDays'
> {
  const [notes, setNotes] = useState(initialData?.notes ?? '')
  const [termsConditions, setTermsConditions] = useState(initialData?.termsConditions ?? '')
  const [paymentTerms, setPaymentTerms] = useState(initialData?.paymentTerms ?? 'Net 30')
  const [selectedPaymentTermValue, setSelectedPaymentTermValue] = useState<string>(() =>
    initialData?.paymentTerms ? parsePaymentTermsToDays(initialData.paymentTerms).selectValue : '30',
  )
  const [customCreditDays, setCustomCreditDays] = useState<string>(() =>
    initialData?.paymentTerms ? parsePaymentTermsToDays(initialData.paymentTerms).customDays : '',
  )
  return {
    notes, setNotes, termsConditions, setTermsConditions, paymentTerms, setPaymentTerms,
    selectedPaymentTermValue, setSelectedPaymentTermValue, customCreditDays, setCustomCreditDays,
  }
}

function useInvoiceMiscState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'poNumber' | 'setPONumber' | 'partyVendorCode' | 'setPartyVendorCode'
  | 'isRoundOffEnabled' | 'setIsRoundOffEnabled' | 'placeOfSupply' | 'setPlaceOfSupply'
> {
  const [poNumber, setPONumber] = useState(initialData?.poNumber ?? '')
  const [partyVendorCode, setPartyVendorCode] = useState(initialData?.partyVendorCode ?? '')
  const [isRoundOffEnabled, setIsRoundOffEnabled] = useState(() => {
    if (initialData?.id && initialData.roundOffAmount === 0) { return false }
    return true
  })
  const [placeOfSupply, setPlaceOfSupply] = useState(initialData?.placeOfSupply ?? '')
  return {
    poNumber, setPONumber, partyVendorCode, setPartyVendorCode,
    isRoundOffEnabled, setIsRoundOffEnabled, placeOfSupply, setPlaceOfSupply,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Customer PO / banks / walk-in / credit limit
// ─────────────────────────────────────────────────────────────────────────────

export function useCustomerPOState(): Pick<InvoiceFormState,
  'customerPOs' | 'setCustomerPOs'
  | 'isCustomerPOSelectEnabled' | 'setIsCustomerPOSelectEnabled'
  | 'selectedCustomerPOId' | 'setSelectedCustomerPOId'
> {
  const [customerPOs, setCustomerPOs] = useState<CustomerPurchaseOrder[]>([])
  const [isCustomerPOSelectEnabled, setIsCustomerPOSelectEnabled] = useState(false)
  const [selectedCustomerPOId, setSelectedCustomerPOId] = useState<string>('')
  return { customerPOs, setCustomerPOs, isCustomerPOSelectEnabled, setIsCustomerPOSelectEnabled, selectedCustomerPOId, setSelectedCustomerPOId }
}

export function useBanksState(): Pick<InvoiceFormState,
  'bankAccounts' | 'setBankAccounts' | 'bankGLAccounts' | 'setBankGLAccounts'
  | 'selectedBankAccountId' | 'setSelectedBankAccountId'
> {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [bankGLAccounts, setBankGLAccounts] = useState<Account[]>([])
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('')
  return { bankAccounts, setBankAccounts, bankGLAccounts, setBankGLAccounts, selectedBankAccountId, setSelectedBankAccountId }
}

export function useWalkinState(): Pick<InvoiceFormState,
  'isWalkIn' | 'setIsWalkIn' | 'cashGLAccounts' | 'setCashGLAccounts'
  | 'selectedCashAccountId' | 'setSelectedCashAccountId'
> {
  const [isWalkIn, setIsWalkIn] = useState<boolean>(false)
  const [cashGLAccounts, setCashGLAccounts] = useState<Account[]>([])
  const [selectedCashAccountId, setSelectedCashAccountId] = useState<string>('')
  return { isWalkIn, setIsWalkIn, cashGLAccounts, setCashGLAccounts, selectedCashAccountId, setSelectedCashAccountId }
}

export function useCreditLimitState(): Pick<InvoiceFormState,
  'partyOutstanding' | 'setPartyOutstanding' | 'creditLimitWarning' | 'setCreditLimitWarning'
> {
  const [partyOutstanding, setPartyOutstanding] = useState<number>(0)
  const [creditLimitWarning, setCreditLimitWarning] = useState<string | null>(null)
  return { partyOutstanding, setPartyOutstanding, creditLimitWarning, setCreditLimitWarning }
}

// ─────────────────────────────────────────────────────────────────────────────
// Export / addresses / modal / shipping / submission
// ─────────────────────────────────────────────────────────────────────────────

export function useExportFieldsState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState, 'exportType' | 'setExportType' | 'lutNumber' | 'setLutNumber' | 'lutDate' | 'setLutDate'> {
  const { settings } = useSettings()
  const [exportType, setExportType] = useState<'goods' | 'services'>(() =>
    defaultExportType(initialData, settings.businessType),
  )
  const [lutNumber, setLutNumber] = useState(initialData?.lutNumber ?? '')
  const [lutDate, setLutDate] = useState<Date | null>(
    initialData?.lutDate ? parseLocalDate(initialData.lutDate) : null,
  )
  return { exportType, setExportType, lutNumber, setLutNumber, lutDate, setLutDate }
}

export function useAddressesState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'shipToType' | 'setShipToType' | 'customShipToAddress'
  | 'consigneeType' | 'setConsigneeType' | 'consigneeName' | 'customConsigneeAddress'
  | 'notifyPartyType' | 'setNotifyPartyType' | 'notifyPartyName' | 'customNotifyPartyAddress'
  | 'entityAddresses' | 'setEntityAddresses'
> {
  const [shipToType, setShipToType] = useState<'same' | 'party' | 'custom'>(() =>
    initialData?.shipToAddress?.line1 ? 'custom' : 'same',
  )
  const [customShipToAddress] = useState<AddressSnapshot>(() =>
    buildAddressSnapshot(initialData?.shipToAddress),
  )
  const [consigneeType, setConsigneeType] = useState<'same' | 'party' | 'custom'>(() =>
    initialData?.consigneeAddress?.line1 ? 'custom' : 'same',
  )
  const [consigneeName] = useState(initialData?.consigneeName ?? '')
  const [customConsigneeAddress] = useState<AddressSnapshot>(() =>
    buildAddressSnapshot(initialData?.consigneeAddress),
  )
  const [notifyPartyType, setNotifyPartyType] = useState<'same' | 'custom' | 'none'>(() =>
    initialData?.notifyPartyAddress?.line1 ? 'custom' : 'none',
  )
  const [notifyPartyName] = useState(initialData?.notifyPartyName ?? '')
  const [customNotifyPartyAddress] = useState<AddressSnapshot>(() =>
    buildAddressSnapshot(initialData?.notifyPartyAddress),
  )
  const [entityAddresses, setEntityAddresses] = useState<Address[]>([])
  return {
    shipToType, setShipToType, customShipToAddress,
    consigneeType, setConsigneeType, consigneeName, customConsigneeAddress,
    notifyPartyType, setNotifyPartyType, notifyPartyName, customNotifyPartyAddress,
    entityAddresses, setEntityAddresses,
  }
}

export interface ReconstructedAddrs {
  reconstructedPartyAddress: Address | null
  reconstructedShipToAddress: Address | null
}

export function useReconstructedAddressMemos(
  initialData: Partial<Invoice> | undefined,
): ReconstructedAddrs {
  const reconstructedPartyAddress = useMemo<Address | null>(
    () => buildReconstructedPartyAddress(initialData),
    [initialData],
  )
  const reconstructedShipToAddress = useMemo<Address | null>(
    () => buildReconstructedShipToAddress(initialData),
    [initialData],
  )
  return { reconstructedPartyAddress, reconstructedShipToAddress }
}

export function useSelectedAddressState(
  initialData: Partial<Invoice> | undefined,
  reconstructed: ReconstructedAddrs,
): Pick<InvoiceFormState,
  'selectedShipToAddress' | 'setSelectedShipToAddress'
  | 'selectedConsigneeAddress' | 'setSelectedConsigneeAddress'
  | 'setSelectedNotifyPartyAddress'
> {
  const [selectedShipToAddress, setSelectedShipToAddress] = useState<Address | null>(
    reconstructed.reconstructedShipToAddress ?? reconstructed.reconstructedPartyAddress ?? null,
  )
  const [selectedConsigneeAddress, setSelectedConsigneeAddress] = useState<Address | null>(
    (initialData?.consigneeAddress as Address | undefined) ?? null,
  )
  const [, setSelectedNotifyPartyAddress] = useState<Address | null>(
    (initialData?.notifyPartyAddress as Address | undefined) ?? null,
  )
  return {
    selectedShipToAddress, setSelectedShipToAddress,
    selectedConsigneeAddress, setSelectedConsigneeAddress,
    setSelectedNotifyPartyAddress,
  }
}

export function useModalState(): Pick<InvoiceFormState,
  'isShipToModalOpen' | 'setIsShipToModalOpen'
  | 'isConsigneeModalOpen' | 'setIsConsigneeModalOpen'
  | 'isQuickCustomerFormOpen' | 'setIsQuickCustomerFormOpen'
  | 'isBillingAddressSelectorOpen' | 'setIsBillingAddressSelectorOpen'
  | 'isShippingAddressSelectorOpen' | 'setIsShippingAddressSelectorOpen'
  | 'isVersionHistoryVisible' | 'setIsVersionHistoryVisible'
> {
  const [isShipToModalOpen, setIsShipToModalOpen] = useState(false)
  const [isConsigneeModalOpen, setIsConsigneeModalOpen] = useState(false)
  const [isQuickCustomerFormOpen, setIsQuickCustomerFormOpen] = useState(false)
  const [isBillingAddressSelectorOpen, setIsBillingAddressSelectorOpen] = useState(false)
  const [isShippingAddressSelectorOpen, setIsShippingAddressSelectorOpen] = useState(false)
  const [isVersionHistoryVisible, setIsVersionHistoryVisible] = useState(false)
  return {
    isShipToModalOpen, setIsShipToModalOpen,
    isConsigneeModalOpen, setIsConsigneeModalOpen,
    isQuickCustomerFormOpen, setIsQuickCustomerFormOpen,
    isBillingAddressSelectorOpen, setIsBillingAddressSelectorOpen,
    isShippingAddressSelectorOpen, setIsShippingAddressSelectorOpen,
    isVersionHistoryVisible, setIsVersionHistoryVisible,
  }
}

export function useShippingState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'countryOfDestination' | 'setCountryOfDestination'
  | 'countryOfDestinationCode' | 'setCountryOfDestinationCode'
  | 'portOfLoading' | 'setPortOfLoading'
  | 'portOfDischarge' | 'setPortOfDischarge'
  | 'shippingBillNumber' | 'setShippingBillNumber'
  | 'shippingBillDate' | 'setShippingBillDate'
  | 'portCode' | 'setPortCode'
  | 'modeOfTransport' | 'setModeOfTransport'
  | 'vesselFlightNumber' | 'setVesselFlightNumber'
  | 'blAwbNumber' | 'setBlAwbNumber'
  | 'containerNumbers' | 'setContainerNumbers'
> {
  return { ...useDestinationState(initialData), ...usePortsState(initialData), ...useTransportState(initialData) }
}

function resolveInitialDestCode(initialData: Partial<Invoice> | undefined): string {
  if (initialData?.countryOfDestinationCode) { return initialData.countryOfDestinationCode }
  if (initialData?.countryOfDestination) {
    const match = countries.find((c) => c.name === initialData.countryOfDestination)
    return match?.code ?? ''
  }
  return ''
}

function useDestinationState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'countryOfDestination' | 'setCountryOfDestination'
  | 'countryOfDestinationCode' | 'setCountryOfDestinationCode'
> {
  const [countryOfDestination, setCountryOfDestination] = useState(initialData?.countryOfDestination ?? '')
  const [countryOfDestinationCode, setCountryOfDestinationCode] = useState(() => resolveInitialDestCode(initialData))
  return { countryOfDestination, setCountryOfDestination, countryOfDestinationCode, setCountryOfDestinationCode }
}

function usePortsState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'portOfLoading' | 'setPortOfLoading'
  | 'portOfDischarge' | 'setPortOfDischarge'
  | 'shippingBillNumber' | 'setShippingBillNumber'
  | 'shippingBillDate' | 'setShippingBillDate'
  | 'portCode' | 'setPortCode'
> {
  const [portOfLoading, setPortOfLoading] = useState(initialData?.portOfLoading ?? '')
  const [portOfDischarge, setPortOfDischarge] = useState(initialData?.portOfDischarge ?? '')
  const [shippingBillNumber, setShippingBillNumber] = useState(initialData?.shippingBillNumber ?? '')
  const [shippingBillDate, setShippingBillDate] = useState<Date | null>(
    initialData?.shippingBillDate ? parseLocalDate(initialData.shippingBillDate) : null,
  )
  const [portCode, setPortCode] = useState(initialData?.portCode ?? '')
  return {
    portOfLoading, setPortOfLoading, portOfDischarge, setPortOfDischarge,
    shippingBillNumber, setShippingBillNumber, shippingBillDate, setShippingBillDate,
    portCode, setPortCode,
  }
}

function useTransportState(
  initialData: Partial<Invoice> | undefined,
): Pick<InvoiceFormState,
  'modeOfTransport' | 'setModeOfTransport'
  | 'vesselFlightNumber' | 'setVesselFlightNumber'
  | 'blAwbNumber' | 'setBlAwbNumber'
  | 'containerNumbers' | 'setContainerNumbers'
> {
  const [modeOfTransport, setModeOfTransport] = useState<'sea' | 'air' | 'road' | 'rail'>(
    initialData?.modeOfTransport ?? 'sea',
  )
  const [vesselFlightNumber, setVesselFlightNumber] = useState(initialData?.vesselFlightNumber ?? '')
  const [blAwbNumber, setBlAwbNumber] = useState(initialData?.blAwbNumber ?? '')
  const [containerNumbers, setContainerNumbers] = useState(initialData?.containerNumbers ?? '')
  return {
    modeOfTransport, setModeOfTransport, vesselFlightNumber, setVesselFlightNumber,
    blAwbNumber, setBlAwbNumber, containerNumbers, setContainerNumbers,
  }
}

export function useSubmissionState(): Pick<InvoiceFormState,
  'isSubmitting' | 'setIsSubmitting'
  | 'errors' | 'setErrors'
  | 'isErrorModalVisible' | 'setIsErrorModalVisible'
  | 'isFetchingRate' | 'setIsFetchingRate'
  | 'currencyChangedByUser'
> {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)
  const [isFetchingRate, setIsFetchingRate] = useState(false)
  const currencyChangedByUser = useRef(false)
  return {
    isSubmitting, setIsSubmitting, errors, setErrors,
    isErrorModalVisible, setIsErrorModalVisible, isFetchingRate, setIsFetchingRate,
    currencyChangedByUser,
  }
}
